/**
 * @desc        Paddocks API
 * @version     1.3 (added default values for paddock details) - 17 Apr 2026 **/

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { shallowEqual, useSelector } from 'react-redux'
import Axios from 'axios'
import { FARMDECK_API, GISSERVICE_API, RESOURCE_API, TIME_LIMIT_FOR_FETCH } from 'app-config/index.js'
import { accountId } from 'helpers/account.js'
import { cloneDeep } from 'lodash'
import { tenantFromPathname } from 'helper-functions/account/general.js'
import fastCompare from 'react-fast-compare'
import { MockData } from 'helper-functions/mock-data/index.js'
import { resourceAPIRequest } from 'api/assets.js'
import { migratePastureState, migratePaddockRecords } from 'helper-functions/paddocks/pasture-state.js'
import { pastureStateToColor } from 'helper-functions/paddocks/general.js'
import { resolvePaddockState, PADDOCK_STATE_TO_GENERAL_STATE_KEY } from 'helper-functions/paddocks/paddock-state'
import { withPaddockFlatDetailDefaults } from 'helper-functions/fmd-entities/defaults/paddock.js'
import { staticMap } from 'helper-functions/maps/gm-static.js'
import { useMemo } from 'react'
import turfAreaFn from '@turf/area'
import { featureCollection, lineString, point, polygon, points } from '@turf/helpers'
/** Resolved per call, never at module load: `accountId` reads the page path for a master user, so a constant is frozen to the page open when this module loaded. */
const baseUrl = () => `${FARMDECK_API.replace(':id', accountId())}`

// Closure variable to keep track of the pending promise
let pendingFetchPaddocksPromise = null

export const fetchPaddocks = createAsyncThunk(
    'paddocks/fetchPaddocks',
    async ({ force = false, cache = true, tenant } = {}, { getState, rejectWithValue, dispatch }) => {
        const { lastFetch } = getState().paddocks
        const timeSinceLastFetch = Date.now() - lastFetch
        if (force || !cache || timeSinceLastFetch > TIME_LIMIT_FOR_FETCH) {
            try {
                if (pendingFetchPaddocksPromise) {
                    // Return the existing pending promise
                    return await pendingFetchPaddocksPromise
                }
                const apiURL = FARMDECK_API.replace(':id', tenant || accountId())
                const url = `${apiURL}paddocks/`
                pendingFetchPaddocksPromise = Axios.get(url).then(async response => {
                    pendingFetchPaddocksPromise = null
                    const paddocks = response.data
                    if (paddocks?.items?.length === 0) {
                        const farmId = getState()?.account?.farmId
                        const farms = getState()?.account?.farms?.data?.items
                        const farm = farmId && farms ? farms.find(f => f.id === farmId) : null
                        if (farm?.name && farm?.latitude && farm?.longitude) {
                            return {
                                items: new MockData().paddocks({ farm }),
                            }
                        }
                    }
                    return paddocks
                })

                const result = await pendingFetchPaddocksPromise

                // Automatically fetch attachments after successful paddock fetch
                if (result?.items?.length > 0) {
                    dispatch(fetchAttachments({ paddocks: result.items, tenant }))
                    dispatch(fetchNDVI({ paddockIds: result.items.map(e => e?.id), tenant }))
                }

                return result
            } catch (error) {
                pendingFetchPaddocksPromise = null
                return rejectWithValue(error.toString())
            }
        } else {
            // Return cached data
            return getState().paddocks.data
        }
    },
    {
        condition: ({ force = false } = {}, { getState }) => {
            const { loading } = getState().paddocks
            if (loading && !force) {
                return false
            }
            if (tenantFromPathname().includes('*')) {
                return false
            }
            return true
        },
    },
)

// Fetch NDVI data
export const fetchNDVI = createAsyncThunk('paddocks/fetchNDVI', async ({ paddockIds, farmId, force = false, tenant } = {}, { getState, rejectWithValue }) => {
    try {
        if (!paddockIds?.length) return []

        const contentKeys = paddockIds.map(id => `gis/${id}/ndvi.png`)
        const res = await resourceAPIRequest({
            body: { contentKeys, timeout: 10_000 },
            tenant: tenant || accountId(),
        })

        return Array.isArray(res) && res.length ? res : []
    } catch (error) {
        return rejectWithValue(error.toString())
    }
})

// Get a single paddock
export const fetchPaddock = createAsyncThunk('paddocks/fetchPaddock', async ({ id, tenant }, { rejectWithValue, dispatch }) => {
    try {
        const apiURL = tenant ? FARMDECK_API.replace(':id', tenant) : baseUrl()
        const url = `${apiURL}paddocks/${id}`
        const response = await Axios.get(url)
        if (response?.data?.id === id) {
            dispatch(fetchNDVI({ paddockIds: [id], tenant }))
            dispatch(fetchGIS({ id, tenant }))
        }
        return response.data
    } catch (error) {
        return rejectWithValue(error.toString())
    }
})

// Add a new paddock
export const createPaddock = createAsyncThunk('paddocks/createPaddock', async ({ data, tenant }, { rejectWithValue }) => {
    try {
        const apiURL = tenant ? FARMDECK_API.replace(':id', tenant) : baseUrl()
        const url = `${apiURL}paddocks/`
        const response = await Axios.post(url, data)
        return response.data
    } catch (error) {
        return rejectWithValue(error.toString())
    }
})

// Update an existing paddock
export const updatePaddock = createAsyncThunk('paddocks/updatePaddock', async ({ id, data, tenant }, { getState, rejectWithValue }) => {
    const originalItems = getState().paddocks.data.items
    try {
        const apiURL = tenant ? FARMDECK_API.replace(':id', tenant) : baseUrl()
        const url = `${apiURL}paddocks/${id}`
        const response = await Axios.put(url, data)
        return response.data
    } catch (error) {
        return rejectWithValue({ error: error.toString(), id, originalItems })
    }
})

// Delete a paddock
export const deletePaddock = createAsyncThunk('paddocks/deletePaddock', async ({ id, tenant }, { getState, rejectWithValue }) => {
    const originalItems = getState().paddocks.data.items
    try {
        const apiURL = tenant ? FARMDECK_API.replace(':id', tenant) : baseUrl()
        const url = `${apiURL}paddocks/${id}`
        await Axios.delete(url)
        return id
    } catch (error) {
        return rejectWithValue({ error: error.toString(), id, originalItems })
    }
})

// Add a note to a paddock
export const createPaddockNote = createAsyncThunk('paddocks/createPaddockNote', async ({ id, data, tenant }, { rejectWithValue }) => {
    try {
        const apiURL = tenant ? FARMDECK_API.replace(':id', tenant) : baseUrl()
        const url = `${apiURL}paddocks/${id}/notes/`
        const response = await Axios.post(url, data)
        return { ...response.data, paddockId: id }
    } catch (error) {
        return rejectWithValue(error.toString())
    }
})

// Delete notes from a paddock
export const deletePaddockNotes = createAsyncThunk('paddocks/deletePaddockNotes', async ({ id, items, tenant }, { rejectWithValue }) => {
    try {
        const apiURL = tenant ? FARMDECK_API.replace(':id', tenant) : baseUrl()
        const url = `${apiURL}paddocks/${id}/notes/delete`
        const response = await Axios.post(url, { items })
        return { ...response.data, id, items, tenant }
    } catch (error) {
        return rejectWithValue(error.toString())
    }
})

// Get GIS data
export const fetchGIS = createAsyncThunk('paddocks/fetchGIS', async ({ id, tenant }, { rejectWithValue }) => {
    try {
        const url = `${GISSERVICE_API.replace(':tenantID', tenant || accountId())}/${id}`
        const response = await Axios.get(url)
        return response.data
    } catch (error) {
        return rejectWithValue(error.toString())
    }
})

// Fetch attachments for paddocks
export const fetchAttachments = createAsyncThunk('paddocks/fetchAttachments', async ({ paddocks, tenant }, { rejectWithValue }) => {
    try {
        if (!paddocks?.length) return []

        const keys = paddocks.flatMap(p => (p?.detail?.attachments?.length ? p.detail.attachments.map(a => (a?.fileName && p?.id ? `fields/${p.id}/${a.fileName}` : null)).filter(Boolean) : []))

        if (!keys.length) return []

        const contentKeys = [...new Set(keys)] // de-dupe
        const res = await resourceAPIRequest({
            body: { contentKeys, timeout: 10_000 },
            tenant,
        })

        return Array.isArray(res) && res.length ? res : []
    } catch (error) {
        return rejectWithValue(error.toString())
    }
})

// Initial state
const initialState = {
    attachments: {
        items: [],
        loading: false,
        error: null,
    },
    data: {
        items: [],
    },
    ndvi: {
        items: [],
        lastFetch: 0,
        expires: 0,
    },
    // gis: {
    //     items: [],
    //     lastFetch: 0,
    //     expires: 0,
    // },
    paddock: {
        data: {},
        gis: {
            data: [],
            lastFetch: 0,
            expires: 0,
        },
    },
    loading: false,
    error: null,
    lastFetch: 0,
    lastError: 0,
}

// Paddocks slice
const paddocksSlice = createSlice({
    name: 'paddocks',
    initialState,
    reducers: {},
    extraReducers: builder => {
        builder
            // Fetch paddocks
            .addCase(fetchPaddocks.pending, state => {
                state.loading = true
            })
            .addCase(fetchPaddocks.fulfilled, (state, action) => {
                state.loading = false
                state.data.items = normalizePaddocks(appendStaticMapToPaddocks(action.payload.items))
                state.lastFetch = Date.now()
                state.error = null
                // Ensure the single-paddock slice exists, but never wipe an already-selected
                // paddock on a list refetch — that blanks the /paddocks/:id detail page.
                if (!state.paddock) {
                    state.paddock = {
                        data: {},
                        gis: {
                            data: [],
                            lastFetch: 0,
                            expires: 0,
                        },
                    }
                }
                if (state?.gis) {
                    state.gis = null
                }
            })
            .addCase(fetchPaddocks.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
                state.lastError = Date.now()
            })
            // Fetch NDVI
            .addCase(fetchNDVI.fulfilled, (state, action) => {
                state.ndvi.items = action.payload
                state.ndvi.error = null
                state.ndvi.lastFetch = Date.now()
            })
            .addCase(fetchNDVI.rejected, (state, action) => {
                state.ndvi.error = action.payload
                state.ndvi.lastError = Date.now()
            })
            // Get paddock
            .addCase(fetchPaddock.fulfilled, (state, action) => {
                const [paddock] = normalizeSinglePaddock(action.payload)
                if (!state.paddock?.data) {
                    state.paddock = {
                        data: {},
                        gis: {
                            data: [],
                            lastFetch: 0,
                            expires: 0,
                        },
                    }
                } else {
                    state.paddock = {
                        data: paddock ? { ...paddock } : {},
                        gis: {
                            data: [],
                            lastFetch: 0,
                            expires: 0,
                        },
                    }
                }
                // state.paddock.data = { ...action.payload }
                // state.error = null
                // state.lastFetch = Date.now()
                // state.loading = false
                // console.log('state.paddock', state.paddock)
                // if (index >= 0) {
                //     state.data.items[index] = action.payload
                // } else {
                //     state.data.items.push(action.payload)
                // }
                // state.error = null
            })
            .addCase(fetchPaddock.rejected, (state, action) => {
                state.error = action.payload
                state.lastError = Date.now()
            })
            // Add paddock
            .addCase(createPaddock.fulfilled, (state, action) => {
                state.data.items.push(action.payload)
                state.error = null
            })
            .addCase(createPaddock.rejected, (state, action) => {
                state.error = action.payload
                state.lastError = Date.now()
            })
            // Update paddock
            .addCase(updatePaddock.fulfilled, (state, action) => {
                if (!action.payload?.id) {
                    state.error = 'Invalid payload: missing id'
                    return
                }

                // Update selected paddock if it matches
                if (state?.paddock?.data?.id === action.payload.id) {
                    state.paddock.data = { ...state.paddock.data, ...action.payload }
                }

                // Update paddock in items array - Immer allows direct mutation
                if (state.data?.items && Array.isArray(state.data.items)) {
                    const index = state.data.items.findIndex(item => item?.id === action.payload.id)
                    if (index !== -1) {
                        state.data.items[index] = { ...state.data.items[index], ...action.payload }
                    }
                }

                state.error = null
            })
            .addCase(updatePaddock.rejected, (state, action) => {
                if (action.payload?.originalItems) {
                    state.data.items = action.payload.originalItems
                }
                state.error = action.payload.error
                state.lastError = Date.now()
            })
            // Delete paddock
            .addCase(deletePaddock.fulfilled, (state, action) => {
                state.data.items = state.data.items.filter(item => item.id !== action.payload)
                state.error = null
            })
            .addCase(deletePaddock.rejected, (state, action) => {
                if (action.payload?.originalItems) {
                    state.data.items = action.payload.originalItems
                }
                state.error = action.payload.error
                state.lastError = Date.now()
            })
            // Add paddock note
            .addCase(createPaddockNote.fulfilled, (state, action) => {
                const { paddockId, ...rest } = action.payload || {}
                const noteId = rest?.id
                const selectedPaddock = { ...state.paddock.data }
                if (noteId) {
                    let newRecords = [...selectedPaddock.records]
                    const selectedRecordIndex = newRecords?.length > 0 ? selectedPaddock.records.findIndex(e => e.id === noteId) : -1
                    if (selectedRecordIndex >= 0 && newRecords[selectedRecordIndex]) {
                        newRecords[selectedRecordIndex] = { ...newRecords[selectedRecordIndex], ...rest }
                    } else {
                        newRecords.push({ ...rest })
                    }
                    selectedPaddock.records = [...newRecords]
                    state.paddock.data = { ...selectedPaddock }
                } else {
                    // ignore
                }
                state.error = null
            })
            .addCase(createPaddockNote.rejected, (state, action) => {
                state.error = action.payload
                state.lastError = Date.now()
            })
            // Delete paddock notes
            .addCase(deletePaddockNotes.fulfilled, (state, action) => {
                const { id, items, tenant } = action.payload || {}
                const selectedPaddock = { ...state.paddock.data }
                // items is an array of note ids, so return the records that are not in the items array
                if (items && selectedPaddock?.id && selectedPaddock?.records) {
                    const newRecords = selectedPaddock.records.filter(e => !items.includes(e.id))
                    selectedPaddock.records = [...newRecords]
                    state.paddock.data = { ...selectedPaddock }
                }
                state.error = null
            })
            .addCase(deletePaddockNotes.rejected, (state, action) => {
                state.error = action.payload
                state.lastError = Date.now()
            })
            // Get GIS data
            .addCase(fetchGIS.fulfilled, (state, action) => {
                const { payload } = action || {}
                if (!state.paddock?.gis) {
                    state.paddock.gis = {
                        data: [],
                        lastFetch: 0,
                        expires: 0,
                    }
                }
                state.paddock.gis.data = [...consolidateAndClamp(payload)]
                state.paddock.gis.error = null
                state.paddock.gis.lastFetch = Date.now()
                // if (payload?.length) {
                //     state.gis.items = [...consolidateAndClamp(payload)]
                //     state.gis.error = null
                //     state.gis.lastFetch = Date.now()
                // } else {
                //     state.gis.items = []
                //     state.gis.error = null
                //     state.gis.lastFetch = Date.now()
                // }
                state.error = null
            })
            .addCase(fetchGIS.rejected, (state, action) => {
                state.error = action.payload
                state.lastError = Date.now()
                if (!state.paddock?.gis) {
                    state.paddock.gis = {
                        data: [],
                        lastFetch: 0,
                        expires: 0,
                    }
                }
                state.paddock.gis.error = action.payload
            })
            // Fetch attachments
            .addCase(fetchAttachments.pending, state => {
                state.attachments.loading = true
            })
            .addCase(fetchAttachments.fulfilled, (state, action) => {
                state.attachments.loading = false
                state.attachments.items = action.payload
                state.attachments.error = null
            })
            .addCase(fetchAttachments.rejected, (state, action) => {
                if (typeof state?.attachments?.loading === 'boolean' && state?.attachments?.loading) {
                    state.attachments.error = action.payload
                    state.attachments.loading = false
                }
            })
    },
})

export default paddocksSlice.reducer

function turfPolygonArea(path) {
    if (!Array.isArray(path) || path.length < 3) return null
    const coords = path.map(pt => [pt.longitude, pt.latitude])
    coords.push(coords[0])
    const poly = polygon([coords])
    return turfAreaFn(poly) / 10000
}

// Legacy records may store area either in hectares (new) or square metres
// (old). With no unit flag on the record we infer it. Anything above this
// threshold is assumed to be m² (1 ha = 10,000 m²) when we have no polygon
// to verify against.
const SQM_HECTARE_THRESHOLD = 1000

function toHectares(value) {
    const n = Number(value)
    if (!Number.isFinite(n) || n <= 0) return null
    return n > SQM_HECTARE_THRESHOLD ? n / 10000 : n
}

// Disambiguate ha vs m² using the polygon as ground truth: turf gives the true
// hectares from the drawn path, so we pick whichever interpretation of the
// stored value matches it. Falls back to the magnitude heuristic when there is
// no usable polygon.
function resolveAreaHectares(rawArea, path) {
    const truth = turfPolygonArea(path)
    const stored = Number(rawArea)
    const hasStored = Number.isFinite(stored) && stored > 0
    if (!hasStored) return truth ?? 0
    if (truth == null || truth <= 0) return toHectares(stored) ?? 0
    const asSqm = stored / 10000
    return Math.abs(asSqm - truth) < Math.abs(stored - truth) ? asSqm : stored
}

function normalizePaddocks(paddocks) {
    if (!paddocks?.length) return []
    return paddocks.map(p => {
        const area = resolveAreaHectares(p?.area, p?.path)
        return {
            ...p,
            area,
            detail: {
                ...withPaddockFlatDetailDefaults({
                    ...p.detail,
                    // Preserved ahead of the migratePastureState() overwrite below so consumers that
                    // need the true pre-migration value (e.g. helper-functions/paddocks/paddock-state.js)
                    // aren't fed an already-lossy mapping (e.g. legacy 'Resting' -> 'Non-Agricultural').
                    pastureStateRaw: p?.detail?.pastureState,
                    pastureState: migratePastureState(p?.detail?.pastureState),
                    arableLand: toHectares(p?.detail?.arableLand) ?? area,
                }),
            },
            ...(p.records ? { records: migratePaddockRecords(p.records) } : {}),
        }
    })
}

function normalizeSinglePaddock(paddock) {
    return normalizePaddocks(appendStaticMapToPaddocks(paddock ? [paddock] : []))
}

function appendStaticMapToPaddocks(paddocks) {
    if (!paddocks?.length) return []
    return paddocks.map(paddock => {
        // Create a deep copy to avoid mutating the original object
        const paddockCopy = cloneDeep(paddock)
        const resolvedState = PADDOCK_STATE_TO_GENERAL_STATE_KEY[resolvePaddockState(paddock)] ?? paddock?.detail?.pastureState
        const ps = pastureStateToColor?.(resolvedState) || {}
        const fillColor = (ps?.fillColor || '#8AC44A').replace('#', '0x')
        const strokeColor = (ps?.strokeColor || '#8AC44A').replace('#', '0x')

        try {
            return {
                ...paddockCopy,
                staticMap: staticMap({
                    size: '240x240',
                    scale: 1,
                    format: 'jpg',
                    path: paddock.path,
                    pathParams: {
                        color: strokeColor,
                        fill: fillColor,
                        weight: 3,
                        zoom: 15,
                    },
                }),
            }
        } catch (error) {
            console.warn('Error generating static map for paddock:', paddock.id, error)
            return paddockCopy
        }
    })
}

// utils
const toNum = v => (v == null || v === '' ? null : Number(v))
const clamp = (x, lo, hi) => Math.min(hi, Math.max(lo, x))
const pct = (arr, p) => {
    if (!arr.length) return null
    const s = [...arr].sort((a, b) => a - b)
    const i = (p / 100) * (s.length - 1)
    const lo = Math.floor(i),
        hi = Math.ceil(i)
    return lo === hi ? s[lo] : s[lo] + (s[hi] - s[lo]) * (i - lo)
}

// config domain from NDVI_CONFIG ([-1,1] for all here)
const INDEXES = ['ndvi', 'evi', 'ndmi', 'ndwi', 'savi', 'ndsi']
const DOMAIN = INDEXES.reduce((m, k) => {
    m[k] = { min: -1, max: 1 }
    return m
}, {})

// treat -100,-100,100 and (optionally) 0,0,0 as no-data
const isNoDataTriplet = (a, b, c, zeroTripletAsNoData = true) => (a === -100 && b === -100 && c === 100) || (zeroTripletAsNoData && a === 0 && b === 0 && c === 0)

function consolidateAndClamp(
    rows,
    {
        tsField = 'captured_on',
        indices = INDEXES,
        strategy = 'hard', // 'hard' | 'winsor' | 'nullify'
        epsilon = 0.02, // tolerance before treating as out-of-range
        zeroTripletAsNoData = false,
        dropNull = false,
        winsorPL = 1,
        winsorPU = 99, // only used when strategy='winsor'
        sortByTsAsc = true,
    } = {},
) {
    // Pre-compute winsor domains if requested
    const obs = {}
    if (strategy === 'winsor') {
        for (const k of indices) obs[k] = []
        for (const r of rows) {
            for (const k of indices) {
                const vals = [toNum(r[`${k}_min`]), toNum(r[`${k}_mean`]), toNum(r[`${k}_max`])].filter(v => v != null)
                // skip no-data patterns
                if (vals.length === 3 && isNoDataTriplet(...vals, zeroTripletAsNoData)) continue
                obs[k].push(...vals)
            }
        }
    }
    const WDOMAIN =
        strategy === 'winsor'
            ? indices.reduce((m, k) => {
                  const arr = obs[k].filter(Number.isFinite)
                  if (!arr.length) {
                      m[k] = DOMAIN[k]
                      return m
                  }
                  m[k] = { min: pct(arr, winsorPL), max: pct(arr, winsorPU) }
                  return m
              }, {})
            : null

    const coerceTriplet = (k, r) => {
        const max = toNum(r[`${k}_max`])
        const mean = toNum(r[`${k}_mean`])
        const min = toNum(r[`${k}_min`])
        if ([max, mean, min].some(v => v == null)) return null
        if (isNoDataTriplet(max, mean, min, zeroTripletAsNoData)) return null

        const { min: lo, max: hi } = strategy === 'winsor' ? WDOMAIN[k] : DOMAIN[k]

        const outOfRange = v => v < lo - epsilon || v > hi + epsilon

        if (strategy === 'nullify' && [max, mean, min].some(outOfRange)) return null
        if (strategy === 'hard' || strategy === 'winsor') {
            if (Math.abs(mean) > 2) {
                const divider = Math.max(Math.abs(max), Math.abs(min)) / 1
                return { max: max / divider, mean: mean / divider, min: min / divider }
            }
            return { max: clamp(max, lo, hi), mean: clamp(mean, lo, hi), min: clamp(min, lo, hi) }
        }
        return { max, mean, min }
    }

    const out = rows.map(r => {
        const o = { ts: new Date(r[tsField]).getTime() }
        for (const k of indices) {
            const t = coerceTriplet(k, r)
            if (t) o[k] = t
            else if (!dropNull) o[k] = null
        }
        if (dropNull) for (const k of indices) if (o[k] == null) delete o[k]
        return o
    })

    if (sortByTsAsc) out.sort((a, b) => (a.ts > b.ts) - (a.ts < b.ts))
    return out
}

// EXAMPLES
// // 1) Strict clamp to [-1,1] (most stable for NDVI/EVI/NDMI/NDWI/SAVI/NDSI)
// const hard = consolidateAndClamp(example, { strategy: 'hard' })

// // 2) Winsorise to 1–99th pct then clamp (absorbs occasional bad scalings/outliers)
// const winsorised = consolidateAndClamp(example, { strategy: 'winsor', winsorPL: 1, winsorPU: 99 })

// // 3) Nullify any out-of-domain rows instead of clamping (if you prefer gaps)
// const nullified = consolidateAndClamp(example, { strategy: 'nullify' })

/**
 * Hook: returns a stable map of paddock id -> { name } from Redux.
 * Safe to call from any component; returns {} if state is missing or loading.
 * Uses shallow equality to avoid unnecessary re-renders.
 * @returns {Record<string, { name: string }>}
 */
export const useAppSelector = useSelector
export const usePaddocks = () => useAppSelector(s => s?.paddocks?.data?.items ?? [])
export const usePaddockMap = ({ full = false } = {}) => {
    const paddocks = usePaddocks()
    return useMemo(() => {
        return paddocks.reduce((acc, paddock) => {
            acc[paddock.id] = full ? { ...paddock } : { name: paddock.name, farmId: paddock.farmId }
            return acc
        }, {})
    }, [full, paddocks])
}
